/**
 * The online protocol between the game and a room (workers/rooms): JSON messages over a
 * WebSocket, plus the small HTTP API of functions/api/rooms. A room runs the match with the
 * engine and sends each player only its own view (§14); the seed and the full replay are revealed
 * when the match is over.
 */
import {
  canonicalAction,
  type Action,
  type MatchEvent,
  type MatchResult,
  type MatchView,
  type PresetId,
  type Replay,
  type Side,
} from '../engine';

export const PROTOCOL_VERSION = 1;

/** Presets offered for online games. */
export const ONLINE_PRESETS: readonly Exclude<PresetId, 'CUSTOM'>[] = ['A', 'B', 'BASELINE'];

export type Role = Side | 'spectator';

/** A seat as everyone sees it. */
export interface SeatInfo {
  readonly name: string;
  readonly connected: boolean;
  /** Rating before this game, when the player has one. */
  readonly rating: number | null;
}

export interface RatingChange {
  readonly before: number;
  readonly after: number;
}

export interface RoomResult {
  readonly winner: Side | null;
  readonly reason: MatchResult['reason'] | 'abandoned';
  /** Rating changes of a rated game. */
  readonly ratings: Readonly<Record<Side, RatingChange>> | null;
}

/** What anyone with the link may know about a room. */
export interface RoomInfo {
  readonly id: string;
  /** A game between two players, or a shared replay of a match played elsewhere. */
  readonly kind: 'game' | 'replay';
  readonly status: 'waiting' | 'playing' | 'finished';
  readonly preset: string;
  readonly createdAt: string;
  readonly seats: Readonly<Record<Side, SeatInfo | null>>;
  readonly spectators: number;
  /** Since when each absent player has been away (ISO 8601), while the game is on. */
  readonly away: Readonly<Partial<Record<Side, string>>>;
  readonly result: RoomResult | null;
}

export type ClientMessage =
  | {
      readonly t: 'hello';
      readonly v: number;
      readonly player: string;
      readonly key: string;
      readonly name: string;
    }
  /** `seq` is the number of actions the client has seen; stale actions are refused. */
  | { readonly t: 'act'; readonly seq: number; readonly action: Action }
  /** Ends the game in the claimer's favour when the opponent has been away long enough. */
  | { readonly t: 'claim' }
  /** After the game: a new room with the same players, sides swapped. */
  | { readonly t: 'rematch' }
  | { readonly t: 'ping' };

export type ServerMessage =
  | { readonly t: 'welcome'; readonly role: Role; readonly room: RoomInfo }
  /**
   * The match as the receiver may see it. `log` is every event so far on a full update (after
   * joining or reconnecting), `events` only the latest action's on an incremental one.
   */
  | {
      readonly t: 'state';
      readonly seq: number;
      readonly view: MatchView;
      readonly events: readonly MatchEvent[];
      readonly log?: readonly MatchEvent[];
      readonly lastActor: Side | null;
    }
  | { readonly t: 'room'; readonly room: RoomInfo }
  /** The match is over: the whole replay, seed included. */
  | { readonly t: 'end'; readonly replay: Replay; readonly room: RoomInfo }
  /** A rematch room exists: both players may join it. */
  | { readonly t: 'rematch'; readonly id: string; readonly by: Side }
  | { readonly t: 'error'; readonly code: ErrorCode; readonly message: string }
  | { readonly t: 'pong' };

export type ErrorCode =
  | 'bad-message'
  | 'bad-version'
  | 'bad-key'
  | 'not-seated'
  | 'not-your-turn'
  | 'stale'
  | 'illegal'
  | 'not-playing'
  | 'too-early'
  /** The name is not allowed (src/online/names.ts): the player chooses another. */
  | 'bad-name'
  /** Another player has that name, or one that reads alike. */
  | 'name-taken'
  /** The room could not store what was asked (its storage failed): nothing changed. */
  | 'unavailable';

/** How long a player must be away before the opponent may claim the game. */
export const ABANDON_MS = 90_000;

/** Largest message a room accepts. */
export const MAX_MESSAGE_BYTES = 64 * 1024;

/** Room ids: ten letters and digits without look-alikes (about 49 bits). */
export const ROOM_ID_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';
export const ROOM_ID = /^[a-hjkmnp-z2-9]{10}$/;

export function newRoomId(random: (n: number) => Uint8Array): string {
  const bytes = random(10);
  let id = '';
  for (const b of bytes) id += ROOM_ID_ALPHABET[b % ROOM_ID_ALPHABET.length];
  return id;
}

/** Player ids are random: 8–64 letters, digits and dashes. */
export const PLAYER_ID = /^[A-Za-z0-9-]{8,64}$/;
/** Player keys are 32 random bytes in hex. */
export const PLAYER_KEY = /^[0-9a-f]{64}$/;

/** Ids of the automated deployment checks: their games are never rated. */
export const isCheckPlayer = (player: string): boolean => player.startsWith('ci-check-');

export function parseClientMessage(raw: string): ClientMessage | null {
  if (raw.length > MAX_MESSAGE_BYTES) return null;
  let m: unknown;
  try {
    m = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof m !== 'object' || m === null) return null;
  const o = m as Record<string, unknown>;
  switch (o.t) {
    case 'hello':
      return typeof o.v === 'number' &&
        typeof o.player === 'string' &&
        typeof o.key === 'string' &&
        typeof o.name === 'string'
        ? { t: 'hello', v: o.v, player: o.player, key: o.key, name: o.name }
        : null;
    case 'act': {
      // Rebuilt from its known fields: nothing else a client adds ever reaches the replay.
      const action = canonicalAction(o.action);
      return typeof o.seq === 'number' && Number.isInteger(o.seq) && o.seq >= 0 && action
        ? { t: 'act', seq: o.seq, action }
        : null;
    }
    case 'claim':
      return { t: 'claim' };
    case 'rematch':
      return { t: 'rematch' };
    case 'ping':
      return { t: 'ping' };
    default:
      return null;
  }
}
